/**
 * IRI minting and parsing (spec §6). Mirrors `packages/projection/kss_projection/iri.py`;
 * both are covered by tests with the same cases. Bases are constants, never hostname-derived.
 */
export const KM_NS = "https://w3id.org/kss/ns#";
export const KM_SHAPES_NS = "https://w3id.org/kss/shapes#";
export const KM_IMPORTS_BASE = "https://w3id.org/kss/imports/";

const URN = "urn:km:";

export type TreeIdString = `${number}@${number}` | string;

export const km = (local: string) => KM_NS + local;

export const workspaceIri = (ws: string) => `${URN}workspace:${ws}`;
export const docIri = (doc: string) => `${URN}doc:${doc}`;
/** A folder of the page tree (spec §7.5, M24): the workspace and the folder's tree node. */
export const folderIri = (ws: string, treeId: string) => `${URN}folder:${ws}:${treeId}`;
export const blockIri = (doc: string, treeId: string) => `${URN}blk:${doc}:${treeId}`;
export const structureIri = (doc: string, treeId: string) => `${blockIri(doc, treeId)}#structure`;
export const reactionIri = (doc: string, treeId: string) => `${blockIri(doc, treeId)}#reaction`;
export const participationIri = (doc: string, treeId: string, n: number) => `${blockIri(doc, treeId)}#part:${n}`;
export const conditionIri = (doc: string, treeId: string, entryId: string) => `${blockIri(doc, treeId)}#cond:${entryId}`;
export const checklistItemIri = (doc: string, treeId: string, itemId: string) => `${blockIri(doc, treeId)}#item:${itemId}`;
export const webSnapshotIri = (doc: string, treeId: string, snapshotId: string) => `${blockIri(doc, treeId)}#snapshot:${snapshotId}`;
export const quantityIri = (doc: string, treeId: string, entryId: string) => `${blockIri(doc, treeId)}#qty:${entryId}`;
export const annotationIri = (doc: string, annId: string) => `${URN}ann:${doc}:${annId}`;
export const annotationTargetIri = (doc: string, annId: string) => `${annotationIri(doc, annId)}#target`;
export const annotationSelectorIri = (doc: string, annId: string) => `${annotationIri(doc, annId)}#selector`;
export const annotationQuoteIri = (doc: string, annId: string) => `${annotationIri(doc, annId)}#quote`;
export const linkIri = (doc: string, linkId: string) => `${URN}link:${doc}:${linkId}`;
export const wsLinkIri = (ws: string, linkId: string) => `${URN}link:ws:${ws}:${linkId}`;
export const sequenceIri = (ws: string, seqId: string) => `${URN}seq:${ws}:${seqId}`;
export const seqMembershipIri = (ws: string, seqId: string, memberId: string) =>
  `${URN}seqm:${ws}:${seqId}:${memberId}`;
export const conceptIri = (ws: string, conceptId: string) => `${URN}concept:${ws}:${conceptId}`;
export const schemeIri = (ws: string) => `${URN}scheme:${ws}`;
export const userIri = (userId: string) => `${URN}user:${userId}`;
export const runIri = (runId: string) => `${URN}run:${runId}`;
export const assetIri = (sha256Hex: string) => `${URN}asset:sha256:${sha256Hex}`;

export const ontologyGraph = (name: string) => `${URN}graph:ontology:${name}`;
export const shapesGraph = () => `${URN}graph:shapes`;
export const mappingsGraph = () => `${URN}graph:mappings`;
export const assertedGraph = (doc: string) => `${URN}graph:asserted:${doc}`;
export const wsAssertedGraph = (ws: string) => `${URN}graph:asserted:ws:${ws}`;
export const computedGraph = (kind: string, doc: string, treeId: string) =>
  `${URN}graph:computed:${kind}:${doc}:${treeId}`;
export const derivedGraph = (runId: string, doc: string, treeId: string) =>
  `${URN}graph:derived:${runId}:${doc}:${treeId}`;
export const runsGraph = () => `${URN}graph:runs`;

export type ParsedKind =
  | "workspace"
  | "doc"
  | "folder"
  | "block"
  | "structure"
  | "reaction"
  | "participation"
  | "condition"
  | "quantity"
  | "checklist_item"
  | "web_snapshot"
  | "entity"
  | "derived_fact"
  | "descriptor"
  | "annotation"
  | "annotation_part"
  | "link"
  | "ws_link"
  | "sequence"
  | "seq_membership"
  | "concept"
  | "scheme"
  | "user"
  | "run"
  | "asset"
  | "graph";

export interface ParsedIri {
  kind: ParsedKind;
  workspace?: string;
  doc?: string;
  treeId?: string;
  id?: string;
  fragment?: string;
  graphKind?: string;
  parts?: string[];
}

export function isTreeId(value: string): boolean {
  return /^\d+@\d+$/.test(value);
}

const SUB_NODES: Record<string, ParsedKind> = {
  part: "participation",
  cond: "condition",
  qty: "quantity",
  item: "checklist_item",
  snapshot: "web_snapshot",
  ent: "entity",
  xfact: "derived_fact",
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** `#part:<n>`, `#cond:<entryId>`, `#qty:<entryId>` on a reaction block, `#item:<TreeID>` on a
 *  checklist and `#snapshot:<uuid>` on a web page block (spec §6.2). */
function blockSubNode(fragment: string | undefined): { kind: ParsedKind; id: string } | null {
  if (!fragment) return null;
  const colon = fragment.indexOf(":");
  if (colon < 0) return null;
  const kind = SUB_NODES[fragment.slice(0, colon)];
  const id = fragment.slice(colon + 1);
  if (!kind || !id || (kind === "participation" && !/^\d+$/.test(id)) || (kind === "checklist_item" && !isTreeId(id)) || (kind === "web_snapshot" && !UUID.test(id)) || ((kind === "entity" || kind === "derived_fact") && !/^[0-9a-f]{16}$/.test(id))) return null;
  return { kind, id };
}

export function parseIri(iri: string): ParsedIri | null {
  if (!iri.startsWith(URN)) return null;
  let rest = iri.slice(URN.length);
  let fragment: string | undefined;
  const hash = rest.indexOf("#");
  if (hash >= 0) {
    fragment = rest.slice(hash + 1);
    rest = rest.slice(0, hash);
  }
  const [kind, ...args] = rest.split(":");
  switch (kind) {
    case "workspace":
      return args.length === 1 ? { kind: "workspace", workspace: args[0] } : null;
    case "doc":
      if (args.length !== 1) return null;
      return { kind: "doc", doc: args[0] };
    case "folder":
      return args.length === 2 ? { kind: "folder", workspace: args[0], treeId: args[1] } : null;
    case "blk":
      if (args.length !== 2) return null;
      {
        // [M32] a CHEMINF descriptor of the structure or of a participation (spec §11.6)
        const desc = /^(?:structure|part:\d+):(inchikey|inchi|smiles):[0-9a-f]{16}$/.exec(fragment ?? "");
        if (desc) return { kind: "descriptor", doc: args[0], treeId: args[1], id: desc[1], fragment };
      }
      if (fragment === "structure" || fragment === "reaction") return { kind: fragment, doc: args[0], treeId: args[1], fragment };
      {
        const sub = blockSubNode(fragment);
        if (sub) return { kind: sub.kind, doc: args[0], treeId: args[1], id: sub.id, fragment };
      }
      return { kind: "block", doc: args[0], treeId: args[1], fragment };
    case "ann":
      if (args.length !== 2) return null;
      return { kind: fragment ? "annotation_part" : "annotation", doc: args[0], id: args[1], fragment };
    case "link":
      if (args.length === 2) return { kind: "link", doc: args[0], id: args[1] };
      if (args.length === 3 && args[0] === "ws") return { kind: "ws_link", workspace: args[1], id: args[2] };
      return null;
    case "seq":
      return args.length === 2 ? { kind: "sequence", workspace: args[0], id: args[1] } : null;
    case "seqm":
      return args.length === 3
        ? { kind: "seq_membership", workspace: args[0], id: args[2], parts: [args[1]!] }
        : null;
    case "concept":
      return args.length === 2 ? { kind: "concept", workspace: args[0], id: args[1] } : null;
    case "scheme":
      return args.length === 1 ? { kind: "scheme", workspace: args[0] } : null;
    case "user":
      return args.length === 1 ? { kind: "user", id: args[0] } : null;
    case "run":
      return args.length === 1 ? { kind: "run", id: args[0] } : null;
    case "asset":
      return args.length === 2 && args[0] === "sha256" ? { kind: "asset", id: args[1] } : null;
    case "graph":
      return parseGraph(args);
    default:
      return null;
  }
}

function parseGraph(args: string[]): ParsedIri | null {
  const [gk, ...rest] = args;
  switch (gk) {
    case "ontology":
      return rest.length === 1 ? { kind: "graph", graphKind: "ontology", id: rest[0] } : null;
    case "shapes":
    case "mappings":
    case "inferred":
    case "runs":
      return rest.length === 0 ? { kind: "graph", graphKind: gk } : null;
    case "asserted":
      if (rest.length === 1) return { kind: "graph", graphKind: "asserted", doc: rest[0] };
      if (rest.length === 2 && rest[0] === "ws") return { kind: "graph", graphKind: "asserted_ws", workspace: rest[1] };
      return null;
    case "computed":
      return rest.length === 3
        ? { kind: "graph", graphKind: "computed", doc: rest[1], treeId: rest[2], parts: [rest[0]!] }
        : null;
    case "derived":
      return rest.length === 3 ? { kind: "graph", graphKind: "derived", id: rest[0], doc: rest[1], treeId: rest[2] } : null;
    default:
      return null;
  }
}

/** Owning page doc ID of a page-scoped IRI (reserved for dataset restriction, spec §19.4). */
export function docIdOf(iri: string): string | null {
  const p = parseIri(iri);
  return p?.doc ?? null;
}

/** Provenance class of a named graph IRI (spec §15.5 rule 5). */
export function graphProvenance(graph: string): "asserted" | "computed" | "derived" | "ontology" | "unknown" {
  if (graph.startsWith(`${URN}graph:asserted:`)) return "asserted";
  if (graph.startsWith(`${URN}graph:computed:`)) return "computed";
  if (graph.startsWith(`${URN}graph:derived:`)) return "derived";
  if (graph.startsWith(`${URN}graph:ontology:`)) return "ontology";
  return "unknown";
}
